import {
  formatMonthYear,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import type {
  CnmcApplication,
  CnmcPlan,
  OutboundMonthCount,
} from "./cnmcTypes";
import { exportDestinationOf } from "./cnmcRecipientRules";
import type { RegistryConnection } from "@/types";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";

/**
 * The destination an export recipient's EECS account names, as its input suffix shows it: "🇫🇮 Finland",
 * "🇧🇪 Belgium (Flanders)". "" until the account names a registry CNMC exports to.
 */
export const exportDestinationDisplay = (
  eecs: string,
  destinations: readonly { region: string }[],
  t: (key: string) => string
): string => {
  const region = exportDestinationOf(eecs, destinations);
  if (!region) return "";
  const flag = getFlagEmoji(region.substring(0, 2));
  const name = translateCountryCode(region, t);
  return flag ? `${flag} ${name}` : name;
};

export const hasCnmcConnection = (connections: RegistryConnection[] = []) =>
  connections.some((connection) => connection.integration_name === "CNMC");

/** Displays each reported month once, in production order. */
export const planPeriod = (plan: CnmcPlan, locale: string) =>
  [...new Set(plan.inputs.production_sources.map((source) => source.month))]
    .sort()
    .map((month) => formatMonthYear(month, locale))
    .join(", ");

/** A production month (YYYY-MM-01) as the CNMC forms list it, e.g. "Apr 2026". */
export const formatCnmcMonth = (month: string, locale: string) =>
  new Date(month).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  });

/** A month's GO count as the outbound rows list it, e.g. "Apr 2026 · 1 200". */
export const formatCnmcMonthCount = (
  month: OutboundMonthCount,
  locale: string
) =>
  `${formatCnmcMonth(month.month, locale)} · ${formatNumberWithSpaces(month.go_count)}`;

/** The supplied meter readings, including fractions excluded from issuance. */
export const reportedProduction = (plan: CnmcPlan) =>
  plan.inputs.production_sources.reduce(
    (sum, source) => sum + Number(source.value),
    0
  );

/** Classifies source filings separately from their downstream delivery requests. */
export const isIssuance = (application: CnmcApplication) =>
  ["DOMESTIC_ISSUANCE", "EXPORT_ISSUANCE"].includes(
    application.application_type
  );

/** Counts current issuance files, excluding retired versions. */
export const issuanceFileCount = (plan: CnmcPlan) =>
  plan.applications.filter(
    (application) =>
      isIssuance(application) &&
      !["ABANDONED", "SUPERSEDED"].includes(application.status)
  ).length;

/** A consistent status palette across batches, application cards and linked orders. */
export const statusClass = (status: string) => {
  if (status === "NEEDS_ATTENTION")
    return "bg-danger-subtle text-danger-emphasis";
  if (["COMPLETE", "COMPLETED"].includes(status))
    return "bg-success-subtle text-success-emphasis";
  if (["SUBMITTED", "PENDING", "APPROVED", "IN_PROGRESS"].includes(status))
    return "bg-warning-subtle text-warning-emphasis";
  return "bg-secondary-subtle text-body-secondary";
};

/**
 * Where holders submit their own CNMC filings with their digital certificate. The Sede home: its
 * per-procedure links for the GdO forms are not stable enough to deep-link.
 */
export const CNMC_SEDE_URL = "https://sede.cnmc.gob.es/";
